// The sticky footer under a pricing-card screen: what stands selected, and
// the Continue that commits it. Shared by the quote screen and the
// application's pick-a-package face so a family sees one bar wherever the
// cards are.

import { ChevronRightIcon } from "@heroicons/react/24/outline";

import { ButtonSpinner } from "components/Button";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";

import type { QuotePaymentOption, QuotePlan } from "./api";
import { dollars, optionLabel } from "./payment-copy";

function selectionSummary(
  plan: QuotePlan | null,
  option: QuotePaymentOption | null,
): { title: string; detail: string } {
  if (!plan) {
    return {
      title: "No plan selected yet",
      detail: "Choose a package to continue",
    };
  }
  if (!option) {
    return {
      title: `${plan.name} package`,
      detail: "Choose how you'd like to pay",
    };
  }
  const amount =
    option.recurring_cents !== null
      ? `${dollars(option.recurring_cents)}/mo`
      : dollars(option.due_today_cents);
  const today =
    option.due_today_cents > 0
      ? `${dollars(option.due_today_cents)} today`
      : "nothing due today";
  return {
    title: `${plan.name} package`,
    detail: `${optionLabel(option)} — ${amount} · ${today}`,
  };
}

export default function SelectionBar({
  plan,
  option,
  title,
  continueLabel = "Continue",
  busy,
  error,
  onContinue,
}: {
  plan: QuotePlan | null;
  option: QuotePaymentOption | null;
  /** Overrides the "{plan} package" line. The application's
   * pick-a-package screen shares this bar, so the default has to stay
   * the package name — only a screen that never showed one passes
   * something else. */
  title?: string;
  /** Overrides the button's "Continue". Mid-application faces share
   * this bar, where the neutral verb is right — only a screen whose
   * Continue begins the whole act names it. */
  continueLabel?: string;
  busy: boolean;
  /** Shown under the summary — what stopped the last Continue. */
  error: string | null;
  onContinue: (selection: { plan_slug: string; option_slug: string }) => void;
}) {
  const summary = selectionSummary(plan, option);
  // Only the title is overridable: the detail line is the selection
  // itself, which is true on every screen that renders this bar.
  const heading = plan && title ? title : summary.title;
  return (
    <div className="sticky bottom-0 z-30 border-t border-oat-200 bg-ivory-light shadow-[0_-8px_24px_-16px_rgba(80,58,58,0.4)]">
      <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-3 px-4 py-4 2xs:px-5 sm:flex-row sm:items-center sm:gap-6 sm:px-10">
        <div className="flex-1">
          <p className="text-base font-bold text-soil">{heading}</p>
          <p className="text-[13px] text-oat-700">{summary.detail}</p>
          {error ? (
            <p className="mt-1 text-[13px] font-bold text-cantelope-600">
              {error}
            </p>
          ) : null}
        </div>
        {/* Nothing selected yet fades the button; working keeps it fully
            painted with the spinner (the shared class reads aria-busy). */}
        <button
          type="button"
          disabled={!plan || !option || busy}
          aria-busy={busy || undefined}
          onClick={() => {
            if (plan && option) {
              onContinue({ plan_slug: plan.slug, option_slug: option.slug });
            }
          }}
          className={`relative ${CONTINUE_BUTTON_CLASS}`}
        >
          {/* The chevron is pinned to the button's right edge; the label
              stays truly centered because its margins are symmetric, and
              those margins are what keep it clear of the chevron when the
              button hugs its content. */}
          <span className="mx-3 flex items-center gap-2">
            {busy && <ButtonSpinner />}
            {continueLabel}
          </span>
          <ChevronRightIcon
            className="absolute right-[13px] top-1/2 h-4 w-4 -translate-y-1/2"
            strokeWidth={2.5}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
}
